// Every string PurchaseStatusChip can render, and the shape a caller may replace
// them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// The chip's only words are its label, one per outcome, so the keys are the
// outcomes themselves.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

/**
 * The English defaults, frozen, keyed by outcome.
 *
 * The words the My Purchases screen prints on its cards: `won` for an asset the
 * buyer bought outright, `if` for an if-sale, where the high bid fell short of
 * the seller's reserve and the seller has yet to decide.
 */
export const PURCHASE_STATUS_CHIP_TRANSLATIONS = Object.freeze({
  won: 'Purchased',
  if: 'If-Sale',
});

/**
 * The keys in `translations` that PurchaseStatusChip would not read, as dotted
 * paths.
 *
 * The walk itself is BidCard's, which takes the defaults object as an argument,
 * so this surface passes its own rather than owning a second copy of a
 * comparison that has to behave identically everywhere.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, PURCHASE_STATUS_CHIP_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) return PURCHASE_STATUS_CHIP_TRANSLATIONS;

  return { ...PURCHASE_STATUS_CHIP_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidPurchaseStatusChipI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
